// ProfileEditMode — Profil duzenleme formu (inline)
import { Camera, Save, X } from 'lucide-react'
import { useState } from 'react'
import { API_URL, apiFetch } from '../../config/api'

interface Props {
  profile: { id: number; display_name: string; bio?: string; avatar_url?: string; cover_color?: string }
  onSaved: () => void
  onCancel: () => void
}

export default function ProfileEditMode({ profile, onSaved, onCancel }: Props) {
  const [displayName, setDisplayName] = useState(profile.display_name || '')
  const [bio, setBio] = useState(profile.bio || '')
  const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url || '')
  const [coverColor, setCoverColor] = useState(profile.cover_color || '#667eea')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    try {
      const r = await apiFetch(`${API_URL}/api/upload/avatar`, { method: 'POST', body: fd })
      const d = await r.json() as { url?: string }
      if (d.url) setAvatarUrl(d.url)
    } catch { setError('Avatar upload failed') }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      const r = await apiFetch(`${API_URL}/api/profile/update`, {
        method: 'PUT',
        body: JSON.stringify({ display_name: displayName, bio, avatar_url: avatarUrl, cover_color: coverColor })
      })
      if (r.ok) { onSaved() }
      else { const d = await r.json() as { detail?: string }; setError(d.detail || 'Save failed') }
    } catch { setError('Network error') }
    finally { setSaving(false) }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    background: 'var(--body-bg)', border: '1px solid var(--border-color)',
    color: 'var(--text-primary)', fontSize: 14, outline: 'none'
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 13 }}>{error}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ position: 'relative', width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', background: 'var(--body-bg)', border: '2px solid var(--border-color)', flexShrink: 0 }}>
          {avatarUrl ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, color: 'var(--text-secondary)' }}>{displayName[0]?.toUpperCase()}</div>}
          <label style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', opacity: 0, cursor: 'pointer', transition: 'opacity 0.2s' }} onMouseEnter={e => { e.currentTarget.style.opacity = '1' }} onMouseLeave={e => { e.currentTarget.style.opacity = '0' }}>
            <Camera size={18} color="#fff" />
            <input type="file" accept="image/*" onChange={e => { void handleAvatarUpload(e) }} style={{ display: 'none' }} />
          </label>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Display Name</label>
          <input value={displayName} onChange={e => setDisplayName(e.target.value)} style={inputStyle} placeholder="Your name" />
        </div>
      </div>

      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Bio</label>
        <textarea value={bio} onChange={e => setBio(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Tell about yourself..." />
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Cover Color</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="color" value={coverColor} onChange={e => setCoverColor(e.target.value)} style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }} />
            <input value={coverColor} onChange={e => setCoverColor(e.target.value)} style={{ ...inputStyle, width: 120 }} />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8 }}>
        <button onClick={onCancel} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 16px', borderRadius: 8, background: 'var(--body-bg)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <X size={14} /> Cancel
        </button>
        <button onClick={() => { void handleSave() }} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 16px', borderRadius: 8, background: '#667eea', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          <Save size={14} /> {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}
